import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PASSWORD_MIN, type SessionUser } from '@helpdesk/shared';
import { postJson } from '../api/client.ts';
import { logout } from './layout.tsx';
import { Button, ErrorNote, Input, Notice } from './ui.tsx';

/**
 * Passwort ändern (nur Konten ohne Microsoft 365). Andere Sitzungen des Kontos
 * beendet der Server dabei – wer das alte Passwort kannte, ist danach draußen.
 */
export function ChangePasswordForm({ onDone, currentLabel = 'Bisheriges Passwort' }: { onDone?: () => void; currentLabel?: string }) {
  const qc = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const mismatch = repeat.length > 0 && next !== repeat;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await postJson('/api/auth/change-password', { currentPassword: current, newPassword: next });
      setDone(true);
      setCurrent('');
      setNext('');
      setRepeat('');
      await qc.invalidateQueries({ queryKey: ['me'] });
      onDone?.();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Input label={currentLabel} type="password" value={current} onChange={setCurrent} autoComplete="current-password" />
      <Input
        label="Neues Passwort"
        type="password"
        value={next}
        onChange={setNext}
        autoComplete="new-password"
        hint={`Mindestens ${PASSWORD_MIN} Zeichen. Tipp: zwei, drei Wörter mit Zahl, z. B. „Laser-Halle-42“.`}
      />
      <Input
        label="Neues Passwort wiederholen"
        type="password"
        value={repeat}
        onChange={setRepeat}
        autoComplete="new-password"
        error={mismatch ? 'Die beiden Eingaben stimmen nicht überein.' : undefined}
      />
      {error ? <ErrorNote error={error} /> : null}
      {done ? <Notice tone="ok">Passwort geändert. Andere Geräte sind abgemeldet.</Notice> : null}
      <Button type="submit" disabled={busy || !current || next.length < PASSWORD_MIN || next !== repeat}>
        {busy ? 'Wird gespeichert …' : 'Passwort speichern'}
      </Button>
    </form>
  );
}

/** Erster Login mit Startpasswort: erst ein eigenes Passwort, dann die Anwendung. */
export function ForcedPasswordChange({ user }: { user: SessionUser }) {
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-steel-900 px-4 py-12">
      <div className="w-full max-w-[460px]">
        <div className="mb-8 flex flex-col items-center text-center text-white">
          <img src="/arnold-logo-white.svg" alt="Arnold AG" className="h-[34px] w-auto" />
          <span className="my-5 block h-px w-16 bg-steel-700" aria-hidden />
          <h1 className="text-[24px] tracking-[var(--tracking-caps)] text-white">Eigenes Passwort festlegen</h1>
          <p className="mt-2 text-[13px] text-steel-400">Hallo {user.displayName}. Das Startpasswort gilt nur für die erste Anmeldung.</p>
        </div>
        <div className="ad-card space-y-4 p-7 shadow-[var(--shadow-overlay)]">
          <ChangePasswordForm currentLabel="Startpasswort" />
          <button type="button" className="w-full cursor-pointer text-center text-sm text-[var(--text-muted)] underline" onClick={() => void logout(user.id)}>
            Abbrechen und abmelden
          </button>
        </div>
      </div>
    </div>
  );
}
